import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import type { Player } from "@/lib/types";
import { useRoster } from "@/features/coach/hooks/useRoster";
import { useOneShotModal } from "@/components/useOneShotModal";

/**
 * Roster picker modal (M7.5 E5) — a peer primitive alongside
 * `CoachModal` / `ConfirmDialog`. Backed by M6a's `useRoster`.
 *
 * Two modes:
 *   - `single`: pick one player. `onConfirm` fires with a single
 *     `Player`; closing without a pick cancels.
 *   - `multi`: pick N players up to `maxCount` (defaults to
 *     unbounded). `onConfirm` fires with `Player[]`; an empty
 *     selection is allowed.
 *
 * Used by:
 *   - E1 — player-token bind (single mode, anchored to the
 *     selected token).
 *
 * One-modal-at-a-time discipline: the caller (in focus-mode
 * overlay or a tactical canvas inside a modal) must close any
 * parent overlay before opening this. We can't detect that
 * structurally; the docstring is the contract.
 */

export type RosterPickerModalProps =
  | {
      mode: "single";
      title?: string;
      onConfirm: (player: Player) => void;
      onClose: () => void;
      /** Filter to only active players. Defaults to false. */
      activeOnly?: boolean;
      /** `data-testid` on the dialog. Default `"roster-picker"`. */
      "data-testid"?: string;
    }
  | {
      mode: "multi";
      title?: string;
      maxCount?: number;
      onConfirm: (players: Player[]) => void;
      onClose: () => void;
      activeOnly?: boolean;
      "data-testid"?: string;
    };

export function RosterPickerModal(props: RosterPickerModalProps) {
  useOneShotModal(props.onClose);

  const rosterQuery = useRoster();
  const allPlayers = useMemo(
    () => rosterQuery.data?.players ?? [],
    [rosterQuery.data],
  );
  const players = useMemo(
    () => (props.activeOnly ? allPlayers.filter((p) => p.active) : allPlayers),
    [allPlayers, props.activeOnly],
  );

  // Multi-mode local selection — order is selection order for future
  // callers that need ordered multi-pick semantics.
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (props.mode === "multi" && props.maxCount != null) {
        if (prev.length >= props.maxCount) return prev; // cap reached
      }
      return [...prev, id];
    });
  };

  const handleConfirmMulti = () => {
    if (props.mode !== "multi") return;
    const byId = new Map(players.map((p) => [p.id, p]));
    const ordered = selectedIds
      .map((id) => byId.get(id))
      .filter((p): p is Player => !!p);
    props.onConfirm(ordered);
  };

  const handleSingleClick = (player: Player) => {
    if (props.mode !== "single") return;
    props.onConfirm(player);
  };

  if (typeof document === "undefined") return null;

  const title =
    props.title ?? (props.mode === "single" ? "Pick a player" : "Pick players");

  return createPortal(
    <div
      className="fixed inset-0 z-[150] flex items-center justify-center bg-black/80 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid={props["data-testid"] ?? "roster-picker"}
      data-roster-picker
      onClick={(e) => {
        if (e.target === e.currentTarget) props.onClose();
      }}
    >
      <div className="flex max-h-[80vh] w-full max-w-md flex-col gap-3 rounded border border-rule bg-surface p-5">
        <header className="flex items-start justify-between gap-3">
          <h2 className="text-base font-display tracking-tight text-ink">
            {title}
          </h2>
          <button
            type="button"
            onClick={props.onClose}
            aria-label="Close"
            data-testid="roster-picker-close"
            className="shrink-0 rounded border border-rule bg-canvas px-2.5 py-1 text-sm text-ink transition-colors hover:bg-field"
          >
            ✕
          </button>
        </header>

        {rosterQuery.isLoading ? (
          <p className="text-ink-muted text-sm font-mono">Loading roster…</p>
        ) : players.length === 0 ? (
          <p className="text-ink-muted text-sm">
            No players on this team yet.
          </p>
        ) : (
          <ul
            className="flex-1 overflow-y-auto flex flex-col gap-1"
            data-testid="roster-picker-list"
          >
            {players.map((p) => {
              const checked = selectedIds.includes(p.id);
              return (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() =>
                      props.mode === "single"
                        ? handleSingleClick(p)
                        : toggle(p.id)
                    }
                    data-roster-picker-row={p.id}
                    data-selected={checked}
                    className={[
                      "flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm transition-colors",
                      props.mode === "multi" && checked
                        ? "bg-accent text-accent-ink"
                        : "text-ink hover:bg-field",
                    ].join(" ")}
                  >
                    {props.mode === "multi" ? (
                      <span
                        aria-hidden="true"
                        className={[
                          "inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[0.6rem]",
                          checked
                            ? "border-accent bg-accent text-accent-ink"
                            : "border-rule",
                        ].join(" ")}
                      >
                        {checked ? "✓" : ""}
                      </span>
                    ) : null}
                    <span className="font-mono text-ink-muted shrink-0">
                      {p.jersey_number ? `#${p.jersey_number}` : "—"}
                    </span>
                    <span className="truncate">{p.display_name}</span>
                    {!p.active ? (
                      <span className="ml-auto text-[0.6rem] uppercase text-ink-muted">
                        inactive
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {props.mode === "multi" ? (
          <div className="flex items-center justify-between gap-2">
            <span className="text-ink-muted text-xs">
              {selectedIds.length} selected
              {props.maxCount != null ? ` / ${props.maxCount}` : ""}
            </span>
            <button
              type="button"
              onClick={handleConfirmMulti}
              data-testid="roster-picker-confirm"
              className="rounded bg-accent px-3 py-1.5 text-sm font-display uppercase tracking-wide text-accent-ink transition-colors hover:bg-accent-hover"
            >
              Confirm
            </button>
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
